import { useState, useEffect } from 'react';
import { PropertySearch } from '@/components/property/PropertySearch';
import { PropertyGrid } from '@/components/property/PropertyGrid';
import { PageLayout } from '@/components/layout/PageLayout';
import { ResultsHeader } from '@/components/common/ResultsHeader';
import { useProperties } from '@/hooks/useProperties';
import { usePage } from '@/hooks/usePage';
import { PropertyFilters } from '@/types/property';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SEO } from '@/components/shared/SEO';
import { deserializeFilters, serializeFilters, validateFilters } from '@/utils/urlFilters';
import { showErrorToast } from '@/utils/toast';
import { PageSkeleton } from '@/components/ui/Skeleton';

export default function VentasPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Cargar configuración de la página desde el backend
  const { page, viewData, loading: loadingPage, error: pageError } = usePage('ventas');

  // Initialize filters from URL params using urlFilters utility
  const [filters, setFilters] = useState<PropertyFilters>(() => {
    // Deserialize filters from URL
    const urlFilters = deserializeFilters(searchParams);

    // Validate and clean filters
    const validatedFilters = validateFilters(urlFilters);

    // Set default operation
    validatedFilters.operation = 'VENTA';

    return validatedFilters;
  });

  const { properties, loading: loadingProperties, error: propertiesError } = useProperties({
    filters,
    sortBy: 'publishedAt',
    sortOrder: 'desc'
  });

  // Show toast notification when error occurs
  useEffect(() => {
    if (propertiesError) {
      showErrorToast(propertiesError);
    }
  }, [propertiesError]);

  // Sync filters with URL when browser navigation (back/forward) happens
  useEffect(() => {
    const urlFilters = deserializeFilters(searchParams);
    const validatedFilters = validateFilters(urlFilters);

    // Set default operation
    validatedFilters.operation = 'VENTA';

    // Only update if filters actually changed to avoid infinite loops
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(validatedFilters);
    if (filtersChanged) {
      setFilters(validatedFilters);
    }
  }, [searchParams]); // Re-run when URL changes (back/forward buttons)

  const handleSearch = (newFilters: PropertyFilters) => {
    // Maintain default filters
    const defaultFilters = { operation: 'VENTA' };
    const updatedFilters = { ...defaultFilters, ...newFilters };

    // Validate filters before applying
    const validatedFilters = validateFilters(updatedFilters);
    setFilters(validatedFilters);

    // Serialize filters to URL params using utility
    const params = serializeFilters(validatedFilters);

    // Remove 'operation' from URL if it's the default
    if (validatedFilters.operation === 'VENTA') {
      params.delete('operation');
    }

    setSearchParams(params);
  };

  const handleHeroSearch = (query: string) => {
    navigate(`/ventas?location=${encodeURIComponent(query)}`);
  };

  // Show loading while page config is loading
  if (loadingPage) {
    return <PageSkeleton message="Cargando página..." />;
  }

  // Generate URLs and data
  const currentUrl = `${window.location.origin}/ventas`;
  const propertyCount = properties.length;

  // JSON-LD Structured Data
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${currentUrl}#webpage`,
        "url": currentUrl,
        "name": page?.seoTitle || "Propiedades en Venta",
        "description": page?.seoDescription || `Encuentra ${propertyCount} propiedades en venta. Casas, departamentos y oficinas con excelentes condiciones.`,
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": window.location.origin
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Ventas",
              "item": currentUrl
            }
          ]
        }
      }
    ]
  };

  // Use dynamic data from backend if available, fallback to defaults
  const heroTitle = viewData?.hero?.title || "Propiedades en Venta";
  const heroSubtitle = viewData?.hero?.subtitle || "Encuentra la propiedad perfecta para comprar en las mejores ubicaciones";
  const heroImage = viewData?.hero?.backgroundImage || "/images/hero/default-hero.jpg";
  const heroShowSearch = viewData?.hero?.showSearch ?? true;

  // SEO data
  const seoTitle = page?.seoTitle || `Propiedades en Venta - ${propertyCount} Opciones`;
  const seoDescription = page?.seoDescription || "Descubre propiedades en venta: casas, departamentos y oficinas. Encuentra tu inmueble ideal con las mejores condiciones de compra.";
  const seoKeywords = page?.seoKeywords || ["venta propiedades", "casas venta", "departamentos venta", "propiedades para comprar", "bienes raíces venta", "inmuebles en venta"];

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalPath="/ventas"
        ogImage={heroImage}
      />

      <Helmet>
        {/* Additional OG Tags not covered by SEO component */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Propiedades en venta disponibles" />
        <meta property="og:locale" content="es_ES" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <PageLayout
        hero={{
          title: heroTitle,
          subtitle: heroSubtitle,
          backgroundImage: heroImage,
          showSearch: heroShowSearch,
          onSearch: handleHeroSearch
        }}
        error={propertiesError}
        emptyState={!loadingProperties && properties.length === 0 ? {
          title: "No se encontraron propiedades con los filtros seleccionados",
          description: "Intenta ajustar los filtros para encontrar más opciones"
        } : undefined}
      >
        {/* Property Search Filters */}
        <div className="mb-8">
          <PropertySearch
            onSearch={handleSearch}
            initialFilters={filters}
            filtersConfig={viewData?.filters}
            loading={loadingPage}
          />
        </div>

        <ResultsHeader
          title="Propiedades en Venta"
          count={properties.length}
          loading={loadingProperties}
          itemName="propiedad"
        />

        <PropertyGrid
          properties={properties}
          loading={loadingProperties}
        />
      </PageLayout>
    </>
  );
}
